import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { ProPopo, ProTablePlus } from '@bedrock/eed-react';
import { localPx2rem } from '@mpc/common/src/utils';
import { Button, Tag, Space } from '@bedrock/components';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { Plus } from '@bedrock/icons-react';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import CreateAndEditDetails from '../CustomInspectionDetail';
import dayjs from 'dayjs';
import { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import { useDemandOptions } from '#features/useDemandOptions';
import {
  demandSourceMatchTemplatePage,
  DemandSourceMatchTemplateVo,
} from '#services/api';
import CustomInspectionTemplateDetailPreview, {
  CustomInspectionTemplateDetailPreviewRefType,
} from '../CustomInspectionDetail/PreviewDetail';
import { EllipsisTooltip } from '@mpc/components';

export interface CustomInspectionTemplateListProps {
  selectedRowKeys?: number[]; // 选中行
  onSelectRowChange?: (selectedRowKeys: number[]) => void;
  mode: 'List' | 'Select';
  className?: string;
  defaultFilters?: Partial<Record<'main_platform' | 'product_id', any[]>>; // 默认筛选条件
  fixedFilters?: Partial<Record<'main_platform' | 'product_id', any[]>>; // 固定筛选条件
}

const CustomInspectionTemplateList = forwardRef<
  IProTableRef<DemandSourceMatchTemplateVo>,
  CustomInspectionTemplateListProps
>((props, ref) => {
  const {
    mode,
    selectedRowKeys,
    onSelectRowChange,
    className,
    defaultFilters,
    fixedFilters,
  } = props;
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [type, setType] = useState<'add' | 'edit' | 'preview'>('add');
  const customInspectionTemplateDetailPreviewRef =
    useRef<CustomInspectionTemplateDetailPreviewRefType>(null);
  const tableRef = useRef<IProTableRef<DemandSourceMatchTemplateVo>>(null);

  useImperativeHandle(ref, () => {
    if (tableRef?.current) {
      return {
        ...tableRef.current,
      };
    }
    return {} as any;
  });

  const { optionsMap } = useDemandOptions({
    keys: ['main_platform', 'product'],
  });

  const handleAdd = () => {
    setDetailsData(null);
    setType('add');
    setDetailsVisible(true);
  };

  const handleEdit = (record: any) => {
    setDetailsData(record);
    setType('edit');
    setDetailsVisible(true);
  };

  const handlePreview = (record: any) => {
    customInspectionTemplateDetailPreviewRef.current?.open(record.id);
  };

  const columns: IProTablePlusColumn<DemandSourceMatchTemplateVo>[] = useMemo(
    () => [
      {
        title: '模版名称',
        dataIndex: 'name',
        width: localPx2rem(mode === 'Select' ? 200 : 240),
        fixed: 'left' as const,
        className: 'font-bold',
      },
      {
        title: '合作主平台',
        dataIndex: 'main_platform',
        width: localPx2rem(mode === 'Select' ? 240 : 339),
        searchRender: {
          showCondition: false,
          type: FilterType.SELECT_MULTIPLE,
          defaultValue: defaultFilters?.main_platform,
          filterComponentProps: {
            options: optionsMap.main_platform?.options || [],
          },
        },
        render: (v: string, r: DemandSourceMatchTemplateVo) => {
          return (
            <EllipsisTooltip
              text={r.main_platform?.join('、') || '-'}
              clamp={2}
            />
          );
        },
      },
      {
        title: '产品',
        dataIndex: 'product_id',
        width: localPx2rem(mode === 'Select' ? 360 : 339),
        searchRender:
          mode === 'Select'
            ? undefined
            : {
                defaultValue: defaultFilters?.product_id,
                showCondition: false,
                type: FilterType.SELECT_MULTIPLE,
                filterComponentProps: {
                  showSearch: true,
                  options: optionsMap.product?.options || [],
                  returnOnlyLeaf: true,
                },
              },
        render: (v: string, r: DemandSourceMatchTemplateVo) => {
          return (
            <EllipsisTooltip
              text={r.products?.map((item) => item.name).join('、') || '-'}
              clamp={2}
            />
          );
        },
      },
      {
        title: '更新人',
        dataIndex: 'update_user',
        width: localPx2rem(100),
        hideInTable: mode === 'Select',
        render: (v: string, r: DemandSourceMatchTemplateVo) => {
          return (
            <ProPopo
              name={r.updated_by_user?.name}
              mail={r.updated_by_user?.email}
            />
          );
        },
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        width: localPx2rem(180),
        hideInTable: mode === 'Select',
        render: (v: string) => {
          return v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-';
        },
      },
      {
        title: '状态',
        dataIndex: 'state',
        width: localPx2rem(64),
        fixed: 'right' as const,
        hideInTable: mode === 'Select',
        render: (v: boolean) => {
          return v ? (
            <Tag text="启用" size="xSmall" color="light-green" />
          ) : (
            <Tag text="禁用" color="light-grey" size="xSmall" />
          );
        },
        searchRender:
          mode === 'List'
            ? {
                showCondition: false,
                type: FilterType.SELECT,
                filterComponentProps: {
                  options: [
                    {
                      name: '启用',
                      value: true,
                    },
                    {
                      name: '禁用',
                      value: false,
                    },
                  ],
                },
              }
            : undefined,
      },
      {
        title: '操作',
        dataIndex: 'action',
        width: localPx2rem(64),
        fixed: 'right',
        render: (_: any, record: any) => {
          return (
            <Space size={8}>
              {mode === 'List' && (
                <Button type="link" onClick={() => handleEdit(record)}>
                  编辑
                </Button>
              )}
              {mode === 'Select' && (
                <Button type="link" onClick={() => handlePreview(record)}>
                  预览
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [
      mode,
      optionsMap.main_platform?.options,
      optionsMap.product?.options,
      handleEdit,
      handlePreview,
      defaultFilters,
    ]
  );

  const queryService = async (query: any) => {
    const params = query?.params;
    const {
      current,
      pageSize: size,
      search_text,
      paramsWithConditions,
    } = params;
    const conditions = Object.entries(paramsWithConditions || {}).reduce(
      (pre: any, [key, value]: [string, any]) => {
        pre[key] = value.value;
        return pre;
      },
      {}
    );
    try {
      const res = await demandSourceMatchTemplatePage({
        ...(fixedFilters || {}),
        current,
        limit: size,
        ...conditions,
        search_text,
        state: mode === 'Select' ? true : conditions.state,
      });

      return {
        data: {
          list: res.data?.records || [],
          total: res.data?.total,
        },
      };
    } catch (error) {
      console.log('error', error);
      return { data: { list: [], total: 0 } };
    }
  };

  const updatePage = () => {
    tableRef?.current?.query?.();
  };

  useEffect(() => {
    if (!detailsVisible) {
      setDetailsData(null);
    }
  }, [detailsVisible]);

  return (
    <>
      {detailsVisible ? (
        <CreateAndEditDetails
          detailsId={detailsData?.id}
          type={type}
          updatePage={updatePage}
          setDetailsVisible={setDetailsVisible}
          visible={detailsVisible}
          mainPlatformOptions={optionsMap.main_platform?.options || []}
          productOptions={optionsMap.product?.options || []}
        />
      ) : (
        <ProTablePlus
          className={className}
          rowKey="id"
          ref={tableRef}
          scroll={{ x: 'max-content' }}
          request={{ queryService }}
          columns={columns}
          stickyPagination
          sticky={{
            offsetHeader: localPx2rem(-16) as number,
          }}
          pagination={{
            showSizeChanger: mode === 'List',
            isShowJumper: mode === 'List',
            pageSize: mode === 'List' ? 20 : 10,
          }}
          toolbar={{
            title: mode === 'List' ? '定制排查模板' : null,
            filterSetting: {
              disableAddButton: mode === 'Select',
            },
            actionsInTitle: true,
            search: {
              placeholder: '请输入模版名称',
              id: 'search_text',
              searchOnBlur: true,
            },
            actions:
              mode === 'List'
                ? [
                    <Button type="primary" icon={<Plus />} onClick={handleAdd}>
                      新增定制排查模板
                    </Button>,
                  ]
                : undefined,
          }}
          rowSelection={
            mode === 'Select'
              ? {
                  props: { fixed: true, width: localPx2rem(40) },
                  selectedRowKeys: selectedRowKeys || [],
                  type: 'radio',
                  onSelect: (row) => {
                    onSelectRowChange?.([row.id]);
                  },
                }
              : undefined
          }
        />
      )}
      <CustomInspectionTemplateDetailPreview
        ref={customInspectionTemplateDetailPreviewRef}
      />
    </>
  );
});

export default CustomInspectionTemplateList;
